/**
 * Elenco a comparsa per collegare una chat a un Lead/Cliente: prima le schede con
 * lo stesso numero della chat (nome, Lead/Cliente, fase, assegnatario), poi la
 * ricerca libera per nome o numero, infine "Crea nuovo Lead/Cliente".
 */
import { useEffect, useRef, useState } from 'react';
import { Building2, Plus, Search, Loader2 } from 'lucide-react';
import { tip } from '@/components/messages/ChatTooltip';
import { candidateDetails, type ChatNumberCandidate } from '@/lib/services/whatsapp/chatNumberMatch';
import { useClientFreeSearch } from './useChatClientCandidates';

const ROW = 'w-full px-3 py-2 text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 flex items-center gap-2 transition-colors';
const SECTION = 'px-3 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wide text-[#6F6E69]';

interface ClientLinkPickerProps {
  organizationId: string;
  /** Schede con lo stesso numero della chat (anche nessuna) */
  candidates: ChatNumberCandidate[];
  onPick: (clientId: string) => void;
  /** Assente se l'utente non puo' creare Lead ne' Clienti */
  onCreate?: () => void;
}

function KindBadge({ kind }: { kind: 'Lead' | 'Cliente' }) {
  if (kind !== 'Lead') return null;
  return (
    <span className="flex-shrink-0 px-1 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
      Lead
    </span>
  );
}

export function ClientLinkPicker({ organizationId, candidates, onPick, onCreate }: ClientLinkPickerProps) {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { results, searching } = useClientFreeSearch(organizationId, text);
  const shownIds = new Set(candidates.map((c) => c.id));
  const others = results.filter((r) => !shownIds.has(r.id));

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="absolute top-full mt-1 left-0 z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-[rgba(55,53,47,0.09)] dark:border-white/10 w-[300px] max-h-[380px] flex flex-col">
      <div className="overflow-y-auto flex-1">
        {candidates.length > 0 && (
          <div className="border-b border-[rgba(55,53,47,0.09)] dark:border-white/10 pb-1">
            <div className={SECTION}>Con questo numero</div>
            {candidates.map((c) => (
              <button key={c.id} type="button" onClick={() => onPick(c.id)} className={ROW} {...tip(`Collega ${c.name} a questa chat`)}>
                <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ backgroundColor: c.stageColor || '#9B9A97' }} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs font-medium text-[#37352F] dark:text-white truncate">{c.name}</p>
                    <KindBadge kind={c.kind} />
                  </div>
                  <p className="text-[10px] text-[#6F6E69] truncate">{candidateDetails(c)}</p>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="p-2">
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[rgba(55,53,47,0.04)] dark:bg-white/5">
            <Search className="w-3.5 h-3.5 text-[#6F6E69]" />
            <input
              ref={inputRef}
              type="text"
              placeholder={candidates.length > 0 ? "Scegli un'altra scheda: nome o numero" : 'Cerca un Lead o un Cliente...'}
              aria-label="Cerca un Lead o un Cliente"
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="flex-1 text-xs bg-transparent outline-none text-[#37352F] dark:text-white placeholder:text-[#6F6E69]"
            />
            {searching && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#6F6E69]" />}
          </div>
        </div>

        {text.trim().length >= 2 && !searching && others.length === 0 && (
          <div className="px-4 pb-3 text-center text-xs text-[#6F6E69]">Nessun Lead o Cliente trovato</div>
        )}
        {others.map((c) => (
          <button key={c.id} type="button" onClick={() => onPick(c.id)} className={ROW}>
            <Building2 className="w-3.5 h-3.5 text-[#6F6E69] flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <p className="text-xs font-medium text-[#37352F] dark:text-white truncate">{c.name}</p>
                <KindBadge kind={c.kind} />
              </div>
              {(c.numbers[0] || c.contactName) && (
                <p className="text-[10px] text-[#6F6E69] truncate">{c.numbers[0] ? `+${c.numbers[0]}` : c.contactName}</p>
              )}
            </div>
          </button>
        ))}
      </div>

      {onCreate && (
        <div className="border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 p-2">
          <button
            type="button"
            onClick={onCreate}
            className="w-full flex items-center gap-2 px-3 py-2 text-left rounded-md hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5 transition-colors"
            {...tip("Nome, numero e fonte WhatsApp sono già compilati")}
          >
            <Plus className="w-3.5 h-3.5 text-[#2383E2] flex-shrink-0" />
            <span className="text-xs font-medium text-[#2383E2]">Crea nuovo Lead/Cliente</span>
          </button>
        </div>
      )}
    </div>
  );
}
